import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  Tabs,
  Tab,
  Autocomplete,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Assignment as AssignmentIcon,
  Code as CodeIcon,
  LocalHospital as TestIcon
} from '@mui/icons-material';
import { ChargeEvent, BillableCode, CodeModifier } from '../../types/Billing';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`charge-tabpanel-${index}`}
      aria-labelledby={`charge-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ChargeCapture: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [chargeEvents, setChargeEvents] = useState<ChargeEvent[]>([]);
  const [billableCodes, setBillableCodes] = useState<BillableCode[]>([]);
  const [modifiers, setModifiers] = useState<CodeModifier[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [openChargeDialog, setOpenChargeDialog] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState<ChargeEvent | null>(null);
  const [openCodeDialog, setOpenCodeDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState<BillableCode | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Mock data - in real app, these would be API calls
      const mockChargeEvents: ChargeEvent[] = [
        {
          id: 'ce-001',
          orderId: 'ORD-2024-001',
          testId: 'TEST-CBC',
          cptCode: '85025',
          description: 'Complete Blood Count with Differential',
          quantity: 1,
          unitPrice: 45.00,
          totalAmount: 45.00,
          modifiers: [],
          chargeDate: '2024-01-15T08:00:00Z',
          serviceDate: '2024-01-15T08:00:00Z',
          providerId: 'PROV-001',
          locationId: 'LOC-001',
          patientId: 'PAT-001',
          status: 'pending',
          isReflexTest: false,
          isSendOut: false,
          createdAt: '2024-01-15T08:00:00Z',
          updatedAt: '2024-01-15T08:00:00Z'
        },
        {
          id: 'ce-002',
          orderId: 'ORD-2024-002',
          testId: 'TEST-CMP',
          cptCode: '80053',
          description: 'Comprehensive Metabolic Panel',
          quantity: 1,
          unitPrice: 65.00,
          totalAmount: 65.00,
          modifiers: [],
          chargeDate: '2024-01-15T09:30:00Z',
          serviceDate: '2024-01-15T09:30:00Z',
          providerId: 'PROV-002',
          locationId: 'LOC-001',
          patientId: 'PAT-002',
          status: 'finalized',
          isReflexTest: false,
          isSendOut: false,
          createdAt: '2024-01-15T09:30:00Z',
          updatedAt: '2024-01-15T10:00:00Z'
        }
      ];

      const mockBillableCodes: BillableCode[] = [
        {
          id: 'bc-001',
          code: '85025',
          type: 'CPT',
          description: 'Complete Blood Count with Differential',
          category: 'Hematology',
          isActive: true,
          effectiveDate: '2024-01-01T00:00:00Z',
          relativeValueUnits: 1.2,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'bc-002',
          code: '80053',
          type: 'CPT',
          description: 'Comprehensive Metabolic Panel',
          category: 'Chemistry',
          isActive: true,
          effectiveDate: '2024-01-01T00:00:00Z',
          relativeValueUnits: 2.1,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];

      const mockModifiers: CodeModifier[] = [
        {
          id: 'mod-001',
          code: '26',
          description: 'Professional Component',
          type: 'professional',
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        },
        {
          id: 'mod-002',
          code: 'TC',
          description: 'Technical Component',
          type: 'technical',
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-01T00:00:00Z'
        }
      ];

      setChargeEvents(mockChargeEvents);
      setBillableCodes(mockBillableCodes);
      setModifiers(mockModifiers);
    } catch (error) {
      console.error('Error loading charge capture data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'finalized':
        return 'success';
      case 'billed':
        return 'info';
      case 'paid':
        return 'success';
      case 'denied':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const filteredChargeEvents = chargeEvents.filter(charge => {
    const matchesSearch = charge.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         charge.cptCode.includes(searchTerm) ||
                         charge.orderId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || charge.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleEditCharge = (charge: ChargeEvent) => {
    setSelectedCharge(charge);
    setOpenChargeDialog(true);
  };

  const handleAddCharge = () => {
    setSelectedCharge(null);
    setOpenChargeDialog(true);
  };

  const handleEditCode = (code: BillableCode) => {
    setSelectedCode(code);
    setOpenCodeDialog(true);
  };

  const handleAddCode = () => {
    setSelectedCode(null);
    setOpenCodeDialog(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Charge Capture & Test Coding
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            label="Charge Events" 
            icon={<AssignmentIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Billable Codes" 
            icon={<CodeIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Code Modifiers" 
            icon={<TestIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {/* Charge Events Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                  <TextField
                    label="Search charges"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                    }}
                    sx={{ minWidth: 250 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      label="Status"
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="finalized">Finalized</MenuItem>
                      <MenuItem value="billed">Billed</MenuItem>
                      <MenuItem value="paid">Paid</MenuItem>
                      <MenuItem value="denied">Denied</MenuItem>
                      <MenuItem value="cancelled">Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCharge}
                  >
                    Add Charge
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order ID</TableCell>
                        <TableCell>CPT Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Service Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredChargeEvents.map((charge) => (
                        <TableRow key={charge.id} hover>
                          <TableCell>{charge.orderId}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {charge.cptCode}
                            </Typography>
                            {charge.modifiers.length > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                Modifiers: {charge.modifiers.join(', ')}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {charge.description}
                            </Typography>
                            {charge.isReflexTest && (
                              <Chip label="Reflex" size="small" color="info" sx={{ mt: 0.5 }} />
                            )}
                            {charge.isSendOut && (
                              <Chip label="Send-out" size="small" color="warning" sx={{ mt: 0.5, ml: 0.5 }} />
                            )}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(charge.serviceDate)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight="medium">
                              {formatCurrency(charge.totalAmount)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Qty: {charge.quantity}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={charge.status.charAt(0).toUpperCase() + charge.status.slice(1)}
                              color={getStatusColor(charge.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Charge">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditCharge(charge)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Billable Codes Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Billable Codes Management
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCode}
                  >
                    Add Code
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>RVU</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {billableCodes.map((code) => (
                        <TableRow key={code.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {code.code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={code.type} 
                              size="small" 
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {code.description}
                            </Typography>
                          </TableCell>
                          <TableCell>{code.category}</TableCell>
                          <TableCell>{code.relativeValueUnits?.toFixed(2) || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip 
                              label={code.isActive ? 'Active' : 'Inactive'}
                              color={code.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Code">
                              <IconButton 
                                size="small" 
                                onClick={() => handleEditCode(code)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Code Modifiers Tab */}
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Code Modifiers Management
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                  >
                    Add Modifier
                  </Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modifiers.map((modifier) => (
                        <TableRow key={modifier.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {modifier.code}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {modifier.description}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={modifier.type} 
                              size="small" 
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={modifier.isActive ? 'Active' : 'Inactive'}
                              color={modifier.isActive ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Modifier">
                              <IconButton size="small">
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Add/Edit Charge Dialog - Placeholder for now */}
      <Dialog 
        open={openChargeDialog} 
        onClose={() => setOpenChargeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCharge ? 'Edit Charge Event' : 'Add New Charge Event'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            Charge event creation/editing dialog would be implemented here with full form fields for all charge properties.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenChargeDialog(false)}>Cancel</Button>
          <Button variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Code Dialog - Placeholder for now */}
      <Dialog 
        open={openCodeDialog} 
        onClose={() => setOpenCodeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCode ? 'Edit Billable Code' : 'Add New Billable Code'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1 }}>
            Billable code creation/editing dialog would be implemented here with full form fields for all code properties.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCodeDialog(false)}>Cancel</Button>
          <Button variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChargeCapture;